import { CountrySelector } from '@/components/common/CountrySelector';
import { FormFieldWrapper } from '@/components/forms/FormFieldWrapper';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Control } from 'react-hook-form';
import type { FormAData } from '../types/formA.types';

interface BeneficialOwnerCardProps {
  index: number;
  control: Control<FormAData>;
  onRemove: (index: number) => void;
  canRemove: boolean;
  tForms: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * BeneficialOwnerCard component
 * Displays an individual beneficial owner form card with all required fields
 */
export const BeneficialOwnerCard = ({
  index,
  control,
  onRemove,
  canRemove,
  tForms,
}: BeneficialOwnerCardProps) => {
  return (
    <div className='space-y-4 border rounded-lg p-4'>
      <div className='flex justify-between items-center mb-4'>
        <h5 className='text-sm font-medium'>
          {tForms('kyc.beneficialOwner.ownerNumber', {
            number: index + 1,
          })}
        </h5>
        {canRemove && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => onRemove(index)}
            className='text-destructive hover:text-destructive'
          >
            <Trash2 className='w-4 h-4 mr-1' />
            {tForms('kyc.beneficialOwner.removeButton')}
          </Button>
        )}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`beneficialOwners.${index}.lastName`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('kyc.fields.lastName')}
              name={`beneficialOwners.${index}.lastName`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('kyc.placeholders.lastName')}
                className='min-h-[44px]'
                autoComplete='family-name'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`beneficialOwners.${index}.firstName`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('kyc.fields.firstName')}
              name={`beneficialOwners.${index}.firstName`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('kyc.placeholders.firstName')}
                className='min-h-[44px]'
                autoComplete='given-name'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 gap-4 items-start'>
        <FormField
          control={control}
          name={`beneficialOwners.${index}.address`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('kyc.fields.address')}
              name={`beneficialOwners.${index}.address`}
              required
              error={fieldState.error?.message}
              tooltip={tForms('kyc.tooltips.address')}
            >
              <Input
                {...field}
                placeholder={tForms('kyc.placeholders.address')}
                className='min-h-[44px]'
                autoComplete='street-address'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`beneficialOwners.${index}.postcode`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('kyc.fields.postcode')}
              name={`beneficialOwners.${index}.postcode`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('kyc.placeholders.postcode')}
                className='min-h-[44px]'
                autoComplete='postal-code'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`beneficialOwners.${index}.city`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('kyc.fields.city')}
              name={`beneficialOwners.${index}.city`}
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                placeholder={tForms('kyc.placeholders.city')}
                className='min-h-[44px]'
                autoComplete='address-level2'
                inputMode='text'
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`beneficialOwners.${index}.country`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('kyc.fields.country')}
              name={`beneficialOwners.${index}.country`}
              required
              error={fieldState.error?.message}
            >
              <CountrySelector
                value={field.value}
                onValueChange={field.onChange}
                placeholder={tForms('kyc.placeholders.selectCountry')}
                error={fieldState.error?.message}
                required
                name={`beneficialOwners.${index}.country`}
              />
            </FormFieldWrapper>
          )}
        />

        <FormField
          control={control}
          name={`beneficialOwners.${index}.birthDate`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('kyc.fields.birthDate')}
              name={`beneficialOwners.${index}.birthDate`}
              required
              error={fieldState.error?.message}
            >
              <Input
                type='date'
                value={field.value ? format(field.value, 'yyyy-MM-dd') : ''}
                onChange={e => {
                  const date = e.target.value
                    ? new Date(e.target.value)
                    : undefined;
                  field.onChange(date);
                }}
                className='min-h-[44px]'
                max={format(new Date(), 'yyyy-MM-dd')} // Prevent future dates
                min='1900-01-01' // Reasonable minimum birth year
              />
            </FormFieldWrapper>
          )}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
        <FormField
          control={control}
          name={`beneficialOwners.${index}.nationality`}
          render={({ field, fieldState }) => (
            <FormFieldWrapper
              label={tForms('kyc.fields.nationality')}
              name={`beneficialOwners.${index}.nationality`}
              required
              error={fieldState.error?.message}
            >
              <CountrySelector
                value={field.value}
                onValueChange={field.onChange}
                placeholder={tForms('kyc.placeholders.selectNationality')}
                error={fieldState.error?.message}
                required
                name={`beneficialOwners.${index}.nationality`}
              />
            </FormFieldWrapper>
          )}
        />
      </div>
    </div>
  );
};
